/* eslint-disable react/button-has-type */
import React, { createContext } from 'react';
import PropTypes from 'prop-types';

import { checkChildren } from '@hooks';
import { mergeClasses } from '@utils';

import ButtonIcon from './Button.Icon';
import ButtonText from './Button.Text';

export const ButtonContext = createContext();

export default function Button({
  as: Component,
  children,
  className,
  size,
  variant,
  ...restProps
}) {
  checkChildren(children, [Button.Icon, Button.Text]);

  if (!React.Children.toArray(children).length) {
    throw new Error(
      'Button must have at least one child of either type Button.Text or Button.Icon'
    );
  }

  return (
    <ButtonContext.Provider value={true}>
      <Component
        className={mergeClasses(
          'flex w-fit cursor-pointer items-center justify-between gap-x-1 leading-none no-underline antialiased outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-50',
          {
            'px-2 py-2': size === 'sm',
            'px-3 py-3': size === 'md',
            'px-4 py-4': size === 'lg'
          },
          {
            'rounded-full border-0 bg-gradient-to-r from-green to-[#E0F65A] text-black':
              variant === 'primary',
            'rounded-full border-2 border-solid border-black bg-transparent text-black':
              variant === 'secondary',
            'border-0 bg-transparent text-black': variant === 'tertiary'
          },
          className
        )}
        {...restProps}
      >
        {React.Children.map(children, child => {
          if (child.type !== Button.Text && child.type !== Button.Icon) {
            throw new Error(
              'Button children must be of either type Button.Text or Button.Icon'
            );
          }
          return child;
        })}
      </Component>
    </ButtonContext.Provider>
  );
}

Button.propTypes = {
  as: PropTypes.oneOf(['button', 'a']),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary'])
};

Button.defaultProps = {
  as: 'button',
  className: '',
  size: 'sm',
  variant: 'primary'
};

Button.Icon = ButtonIcon;
Button.Text = ButtonText;
